import {type AxiosResponse} from 'axios';
import {type NavigationTreePlan, type PlanningActionApiActionsGetRequest} from 'client';
import {actionTypeHandler} from 'components/EventsTable/helpers';
import {naturalComparator} from 'helpers/naturalSort';
import {dateReader} from 'helpers/UnitsFormatHandler';
import {useGetNaturalComparator} from 'hooks/useGetNaturalComparator';
import useParseURL from 'hooks/useParseURL';
import {
  type HarvestProcessed,
  type InputProcessed,
  type LiveFishTransactionProcessed,
  type TransferProcessed,
  type DelousingProcessed,
  type ActionProcessed,
  type HarvestOverviewProcessed,
} from 'interfaces';
import {type MutableRefObject, useEffect, useRef, useState} from 'react';
import {type BareFetcher, type Key, type Middleware, type SWRConfiguration, type SWRHook, type SWRResponse} from 'swr';
import {useFilterContext} from 'context/FilterContext';
import {useGetPlanRegions} from 'hooks/useGetPlanRegions';
import {endOfMonth, endOfWeek, endOfYear} from 'date-fns';

export const sortByNameMiddleware: Middleware = (useSWRNext: SWRHook) => {
  return (key, fetcher, config) => {
    const {language} = useParseURL();
    const comparator = naturalComparator(language);
    const swr = useSWRNext(key, fetcher, config);
    if (swr.data && Array.isArray(swr.data)) {
      swr.data.sort((a, b) => comparator(a.name, b.name));
    }
    return swr;
  };
};

export declare type ActionsProcessedFetcher = (
  planId: string[],
  _startDate?: Date,
  _endDate?: Date,
  actionType?: PlanningActionApiActionsGetRequest['actionType'],
  certificates?: PlanningActionApiActionsGetRequest['certificates'],
) => Promise<AxiosResponse<ActionProcessed[]>>;

export declare type SiteActionsProcessedFetcher = (siteId: string) => Promise<AxiosResponse<ActionProcessed[]>>;

export declare type HarvestProcessedFetcher = (id: string) => Promise<AxiosResponse<HarvestProcessed[]>>;
export declare type HarvestOverviewProcessedFetcher = (
  planIds: string[],
) => Promise<AxiosResponse<HarvestOverviewProcessed[]>>;
export declare type InputProcessedFetcher = (id: string) => Promise<AxiosResponse<InputProcessed[]>>;
export declare type DelousingProcessedFetcher = (id: string) => Promise<AxiosResponse<DelousingProcessed[]>>;
export declare type SingleDelousingProcessedFetcher = (id: string) => Promise<AxiosResponse<DelousingProcessed>>;
export declare type TransferProcessedFetcher = (id: string) => Promise<AxiosResponse<TransferProcessed[]>>;
export declare type LiveFishTransactionProcessedFetcher = (
  id: string,
) => Promise<AxiosResponse<LiveFishTransactionProcessed[]>>;

export const dateObjectMiddleware: Middleware = (useSWRNext: SWRHook) => {
  return (key: Key, fetcher: BareFetcher<any> | null, config: SWRConfiguration<any, any, BareFetcher<any>>) => {
    const [isFirstData, setIsFirstData] = useState(false);
    const swr = useSWRNext(key, fetcher, config);
    if (swr.data && Array.isArray(swr.data)) {
      swr.data.forEach((dataEntry) => {
        if ('date' in dataEntry) {
          dataEntry.dateObject = dateReader(dataEntry.date);
        }

        if ('actionType' in dataEntry) {
          dataEntry.cellActionType = actionTypeHandler(dataEntry);
        }
      });
    }

    useEffect(() => {
      if (swr.data && Array.isArray(swr.data) && !isFirstData) {
        swr.data.sort((a, b) => {
          if (a.dateObject && b.dateObject) {
            return a.dateObject.getTime() - b.dateObject.getTime();
          } else {
            return 0;
          }
        });
        setIsFirstData(true);
      }
    }, [swr.data]);

    return swr;
  };
};

const periodHandler = (dataEntry: any) => {
  if ('date' in dataEntry) {
    const dateObject = dateReader(dataEntry.date);
    dataEntry.dateObject = dateObject;
    dataEntry.endOfMonth = endOfMonth(dateObject);
    dataEntry.endOfYear = endOfYear(dateObject);
    dataEntry.endOfWeek = endOfWeek(dateObject, {weekStartsOn: 1});
  }
  // dataEntry.cellActionType = FormSelectionEnum.harvest;
};

export const datePeriodsMiddleware: Middleware = (useSWRNext: SWRHook) => {
  return (key: Key, fetcher: BareFetcher<any> | null, config: SWRConfiguration<any, any, BareFetcher<any>>) => {
    const swr = useSWRNext(key, fetcher, config);
    if (swr.data && Array.isArray(swr.data)) {
      swr.data.forEach((dataEntry) => {
        periodHandler(dataEntry);
      });
    }
    return swr;
  };
};

export declare type KeepSortBySWRResponse<T> = SWRResponse<T, any> & {
  id2indexRef: MutableRefObject<Map<string, number> | undefined>;
};

export const keepSortByIdMiddleware: Middleware = (useSWRNext: SWRHook) => {
  return (key: Key, fetcher: BareFetcher<any> | null, config: SWRConfiguration<any, any, BareFetcher<any>>) => {
    const id2indexRef = useRef<Map<string, number>>();
    const swr = useSWRNext(key, fetcher, config);
    const [isFirstData, setIsFirstData] = useState(false);

    useEffect(() => {
      if (swr.data && Array.isArray(swr.data) && !isFirstData) {
        id2indexRef.current = new Map(swr.data.map((entry, index) => [entry.id, index]));
        setIsFirstData(true);
      }
    }, [swr.data]);

    // Sort data according the order set from outside
    if (id2indexRef.current && swr.data && Array.isArray(swr.data)) {
      const id2index = id2indexRef.current;

      swr.data.sort((a, b) => {
        if (!id2index.has(a.id) || !id2index.has(b.id)) {
          return 0;
        }
        return (id2index.get(a.id) as number) - (id2index.get(b.id) as number);
      });
    }

    return {...swr, id2indexRef};
  };
};

const filterRegionMiddleware: Middleware = (useSWRNext: SWRHook) => {
  return (key: Key, fetcher: BareFetcher<any> | null, config: SWRConfiguration<any, any, BareFetcher<any>>) => {
    const filter = useFilterContext();
    const {planId} = useParseURL();
    const {data: regions, isRegionsEnabled} = useGetPlanRegions(planId);

    const swr = useSWRNext(isRegionsEnabled && !regions ? undefined : key, fetcher, config);

    if (!isRegionsEnabled) {
      return swr;
    }

    let hasRegionKey = false;
    if (Array.isArray(swr.data) && swr.data.length > 0) {
      hasRegionKey = ['regionName', 'regionId', 'region'].some((regionKey) =>
        Object.keys(swr.data[0]).includes(regionKey),
      );
    }

    if (!hasRegionKey) {
      return swr;
    }

    let filteredData = swr.data;
    if (
      filter.selectedRegions !== undefined &&
      filter.selectedRegions.length > 0 &&
      regions &&
      swr.data &&
      Array.isArray(swr.data)
    ) {
      const enabledRegions = filter.selectedRegions;
      filteredData = swr.data.filter((dataEntry) => {
        if ('regionName' in dataEntry) {
          if (!enabledRegions.includes(dataEntry.regionName)) {
            return false;
          }
        }

        return true;
      });
    }

    return {
      ...swr,
      data: filteredData,
    };
  };
};

export const sortSiteCageNaturally: Middleware = (useSWRNext: SWRHook) => {
  return (key, fetcher, config) => {
    const naturalComparator = useGetNaturalComparator();
    const swr = useSWRNext(key, fetcher, config);
    if (swr.data && isPlanTreeNavigation(swr.data)) {
      swr.data.sites?.sort((a, b) => naturalComparator(a.name, b.name));
      swr.data.sites?.forEach((site) => site.cages?.sort((a, b) => naturalComparator(a.name, b.name)));
    }
    return swr;
  };
};

function isPlanTreeNavigation(data: any): data is NavigationTreePlan {
  return 'sites' in data;
}
